import { useCallback, useState } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { GridSearchIcon } from '@mui/x-data-grid';

import { autoFillButtonStyles } from '@/components/styles';
import { Address, HeadOfficeAddress } from '@/features/companies/types/company.types';
import { PLACEHOLDER_MESSAGES } from '@/features/companies/utils/constants/placeholder-messages';
import { isValidPostalCodeFormat, lookupPostalCode } from '@/lib/postal-code-service';
import { getStylesForEditableField } from '@/utils/helpers';

interface AddressSectionProps {
  safeAddress: Address | HeadOfficeAddress;
  originalSafeAddress?: Address | HeadOfficeAddress;
  onUpdateAddressField: (field: keyof Address | keyof HeadOfficeAddress, value: string) => void;
  getFieldError: (fieldName: string) => string | undefined;
  isFieldValid: (fieldName: string) => boolean;
  addressType?: 'currentAddress' | 'headOfficeAddress';
}

const AddressSection = ({
  safeAddress,
  originalSafeAddress,
  onUpdateAddressField,
  getFieldError,
  isFieldValid,
  addressType = 'currentAddress',
}: AddressSectionProps) => {
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Auto-fill address when postal code is complete
  const handlePostalCodeLookup = useCallback(async () => {
    const { postalCode1, postalCode2 } = safeAddress;

    if (!isValidPostalCodeFormat(postalCode1, postalCode2)) {
      return;
    }

    setIsLookingUp(true);
    try {
      const result = await lookupPostalCode(postalCode1, postalCode2);
      if (result) {
        onUpdateAddressField('address1', result.fullAddress);
      }
    } catch (error) {
      console.error('Postal code lookup failed:', error);
    } finally {
      setIsLookingUp(false);
    }
  }, [safeAddress.postalCode1, safeAddress.postalCode2, onUpdateAddressField]);

  // Handle postal code change with auto-lookup
  const handlePostalCodeChange = useCallback(
    (field: 'postalCode1' | 'postalCode2', value: string) => {
      onUpdateAddressField(field, value);

      // Trigger lookup when both codes are complete
      setTimeout(() => {
        const updatedCode1 = field === 'postalCode1' ? value : safeAddress.postalCode1;
        const updatedCode2 = field === 'postalCode2' ? value : safeAddress.postalCode2;

        if (isValidPostalCodeFormat(updatedCode1, updatedCode2)) {
          void handlePostalCodeLookup();
        }
      }, 100);
    },
    [
      onUpdateAddressField,
      safeAddress.postalCode1,
      safeAddress.postalCode2,
      handlePostalCodeLookup,
      safeAddress,
    ]
  );

  return (
    <>
      {/* Postal Code */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          sx={{ width: '40%' }}
          label='郵便番号（前半）'
          value={safeAddress.postalCode1}
          onChange={(e) => handlePostalCodeChange('postalCode1', e.target.value)}
          error={!isFieldValid(`${addressType}.postalCode1`)}
          helperText={getFieldError(`${addressType}.postalCode1`)}
          placeholder={PLACEHOLDER_MESSAGES.POSTAL_CODE_3_DIGITS}
          required
          slotProps={{
            input: {
              inputProps: {
                maxLength: 3,
                pattern: '[0-9-]*',
                inputMode: 'numeric',
              },
              sx: getStylesForEditableField(
                !!originalSafeAddress,
                originalSafeAddress?.postalCode1,
                safeAddress.postalCode1
              ),
            },
          }}
        />
        <TextField
          fullWidth
          sx={{ width: '40%' }}
          label='郵便番号（後半）'
          value={safeAddress.postalCode2}
          onChange={(e) => handlePostalCodeChange('postalCode2', e.target.value)}
          error={!isFieldValid(`${addressType}.postalCode2`)}
          helperText={getFieldError(`${addressType}.postalCode2`)}
          placeholder={PLACEHOLDER_MESSAGES.POSTAL_CODE_4_DIGITS}
          required
          slotProps={{
            input: {
              inputProps: {
                maxLength: 4,
                pattern: '[0-9-]*',
                inputMode: 'numeric',
              },
              sx: getStylesForEditableField(
                !!originalSafeAddress,
                originalSafeAddress?.postalCode2,
                safeAddress.postalCode2
              ),
            },
          }}
        />
        <Button
          variant='outlined'
          onClick={() => {
            void handlePostalCodeLookup();
          }}
          disabled={
            !isValidPostalCodeFormat(safeAddress.postalCode1, safeAddress.postalCode2) ||
            isLookingUp
          }
          startIcon={isLookingUp ? <CircularProgress size={16} /> : <GridSearchIcon />}
          sx={{ ...autoFillButtonStyles, width: '20%' }}
        >
          {isLookingUp ? '検索中...' : '住所自動入力'}
        </Button>
      </Box>

      {/* Address Lines */}
      <TextField
        fullWidth
        required
        label='住所１'
        value={safeAddress.address1}
        onChange={(e) => onUpdateAddressField('address1', e.target.value)}
        error={!isFieldValid(`${addressType}.address1`)}
        helperText={getFieldError(`${addressType}.address1`)}
        sx={{ mb: 2 }}
        placeholder={PLACEHOLDER_MESSAGES.ADDRESS1_INPUT}
        slotProps={{
          input: {
            inputProps: { maxLength: 100 },
            sx: getStylesForEditableField(
              !!originalSafeAddress,
              originalSafeAddress?.address1,
              safeAddress.address1
            ),
          },
        }}
      />

      <TextField
        fullWidth
        required
        label='住所２'
        value={safeAddress.address2}
        onChange={(e) => onUpdateAddressField('address2', e.target.value)}
        error={!isFieldValid(`${addressType}.address2`)}
        helperText={getFieldError(`${addressType}.address2`)}
        sx={{ mb: 2 }}
        placeholder={PLACEHOLDER_MESSAGES.ADDRESS2_INPUT}
        slotProps={{
          input: {
            inputProps: { maxLength: 100 },
            sx: getStylesForEditableField(
              !!originalSafeAddress,
              originalSafeAddress?.address2,
              safeAddress.address2
            ),
          },
        }}
      />

      <TextField
        fullWidth
        label='住所３'
        value={safeAddress.address3 || ''}
        onChange={(e) => onUpdateAddressField('address3', e.target.value)}
        error={!isFieldValid(`${addressType}.address3`)}
        helperText={getFieldError(`${addressType}.address3`)}
        placeholder={PLACEHOLDER_MESSAGES.ADDRESS3_INPUT}
        slotProps={{
          input: {
            inputProps: { maxLength: 100 },
            sx: getStylesForEditableField(
              !!originalSafeAddress,
              originalSafeAddress?.address3,
              safeAddress.address3
            ),
          },
        }}
      />

      {/* Contact Information */}
      {'phone' in safeAddress && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            required
            label='電話'
            value={safeAddress.phone}
            onChange={(e) => onUpdateAddressField('phone', e.target.value)}
            error={!isFieldValid('currentAddress.phone')}
            helperText={getFieldError('currentAddress.phone')}
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 20,
                  pattern: '[0-9-]*',
                  inputMode: 'numeric',
                },
                sx: getStylesForEditableField(
                  !!originalSafeAddress,
                  (originalSafeAddress as Address)?.phone,
                  safeAddress.phone
                ),
              },
            }}
            placeholder={PLACEHOLDER_MESSAGES.PHONE_INPUT}
          />
          <TextField
            fullWidth
            required
            label='FAX'
            value={safeAddress.fax}
            onChange={(e) => onUpdateAddressField('fax', e.target.value)}
            error={!isFieldValid('currentAddress.fax')}
            helperText={getFieldError('currentAddress.fax')}
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 20,
                  pattern: '[0-9-]*',
                  inputMode: 'numeric',
                },
                sx: getStylesForEditableField(
                  !!originalSafeAddress,
                  (originalSafeAddress as Address)?.fax,
                  safeAddress.fax
                ),
              },
            }}
            placeholder={PLACEHOLDER_MESSAGES.FAX_INPUT}
          />
        </Box>
      )}
    </>
  );
};

export default AddressSection;
